import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { TreeNode } from 'primeng/api';

import { BaseView } from '../../base-view';
import { DashboardService } from '../../../service/events/dashboard.service';
import { TratamientosApiService } from '../../../service/api/tratamientos-api.service';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { Filtering } from '../../../service/filtering/filtering';
import { StorageManager } from '../../../common/storage-manager.class';
import { Utils } from '../../../common/utils';
import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import {
    FormTable,
    FormType
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';

import { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';
import { TratamientoLineaModel } from 'src/app/models/tratamientos/tratamiento-linea.model';

import { TratamientosEnvironmentDefinition } from './environment-variables';
import { TratamientosFormDefinition } from './tratamientos-form/form-control/form-definition';

@Component({
    selector: 'app-tratamientos',
    standalone: false,
    templateUrl: './tratamientos.component.html',
    styleUrls: ['./tratamientos.component.scss']
})
export class TratamientosComponent extends BaseView<TratamientosModel> implements OnInit {

    public form: TratamientosFormDefinition | null = null;

    public env: TratamientosEnvironmentDefinition = new TratamientosEnvironmentDefinition();

    public getRequest: RequestHandler<TratamientosModel[]> = this.tratamientosApi.tratamientosList;

    public deleteRequest: RequestHandler<TratamientosModel> = this.tratamientosApi.tratamientosDELETE;

    public filter: Filtering<TratamientosModel> = new Filtering<TratamientosModel>();

    public showAllTratamientos: boolean = false;

    public showAllTratamientosControl = new FormControl<boolean>(false, { nonNullable: true });

    public override canShow: Record<string, boolean> = {};

    public override model: TratamientosModel = {
        nombre: '',
        producto: { nombre: '', materia_activa: '' },
        cantidad: '',
        fecha_act: '',
        finca: '',
        activo: false,
        linea: []
    };

    public readonly btnAnadir: string = 'Añadir Tratamiento';

    public cols: ColInterface[] = [];

    public constructor(
        public tratamientosApi: TratamientosApiService,
        private readonly dashboardEvents: DashboardService,
        private readonly router: Router
    ) {
        super(dashboardEvents, tratamientosApi.tratamientosList, tratamientosApi.tratamientosDELETE);
    }

    public ngOnInit(): void {
        this.cols = [
            {
                field: 'tecnico_nombre',
                header: this.env.appName === 'avafitogest' ? 'Grabador' : 'Técnico',
                filter: '',
                width: '80px'
            },
            { field: 'nombre', header: 'Nombre', filter: '' },
            { field: 'productos', header: 'Productos', filter: '' },
            { field: 'usos', header: 'Usos', filter: '' },
            { field: 'agentes', header: 'Agentes', filter: '' },
            { field: 'fecha_caducidad', header: 'Caducidad', filter: '' },
            {
                field: 'cantidades_cuaderno',
                header: this.env.enableLegalMode ? 'Dosis' : 'Cuaderno',
                filter: '',
                width: '80px',
                visible: !StorageManager.checkHiddenDosisCuaderno()
            },
            {
                field: 'uds_medida',
                header: 'Uds Medida',
                filter: '',
                width: '80px'
            },
            {
                field: 'activo',
                header: 'Activo',
                width: '60px',
                visible: this.env.showAplicadoresActivo
            }
        ];

        if (this.env.showTratamientosMenu) {
            this.dashboardEvents.setMenuItem('tratamientos');
        }

        this.dashboardEvents.setHeaderTitle('Tratamientos');

        this.showAllTratamientos = Utils.initShowAll('showAlltratamientos');
        this.showAllTratamientosControl.setValue(this.showAllTratamientos, { emitEvent: false });
        this.initFilter();
    }

    public changeShowAll(): void {
        this.showAllTratamientos = this.showAllTratamientosControl.value;
        sessionStorage.setItem('showAlltratamientos', this.showAllTratamientos ? 'true' : 'false');
    }

    public filterTratamientos(): void {
        this.filter.filter();
    }

    public add(): void {
        if (this.env.showTareasMto) {
            this.router.navigate(['dashboard', 'crear-tratamiento']);

            return;
        }

        if (!StorageManager.getClient() && this.env.userCanSeeClientes && this.env.hasClients) {
            this.dashboardEvents.openClientDialog();
            return;
        }

        this.router.navigate(['dashboard', 'crear-tratamiento']);
    }

    public edit(data: TratamientosModel): void {
        this.router.navigate(['dashboard', 'editar-tratamiento', data.id]);
    }

    public duplicate(data: TratamientosModel): void {
        this.router.navigate(['dashboard', 'duplicar-tratamiento', data.id]);
    }

    public override show(modal: string, data: TratamientosModel): void {
        const modalModel = this.buildModalModel(data);

        this.model = modalModel;
        super.show(modal, modalModel);

        const formType = modal === 'delete'
            ? +FormType.DELETION
            : +FormType.INFORMATIVE;

        this.form = new TratamientosFormDefinition(
            formType,
            modalModel,
            this.buildDetailTable(modalModel)
        );

        this.form.model = {
            ...this.form.model,
            ...modalModel
        };
    }

    public globalConditionalStyle(
        _value: string,
        col: ColInterface,
        _colGroup: ColInterface[],
        row: TreeNode<TratamientosModel>
    ): ColStylesInterface {
        const tratamiento = row.data as TratamientosModel;

        if (col.field !== 'fecha_caducidad') {
            return { textAlign: 'center' };
        }

        let cellStyle: ColStylesInterface = {
            rowStyle: {
                color: 'black',
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #dddddd'
            },
            textAlign: 'center'
        };

        const fechaInscripcion = String(tratamiento.fecha_inscripcion ?? '');
        const fechaCaducidad = String(tratamiento.fecha_caducidad ?? '');

        const diffInsc = moment(new Date()).diff(Utils.toDate(fechaInscripcion), 'months');

        if (diffInsc < 12) {
            cellStyle = {
                rowStyle: {
                    color: 'black',
                    backgroundColor: '#cbdecb',
                    borderBottom: '1px solid #dddddd'
                },
                textAlign: 'center'
            };
        }

        const diffCaducidad = moment(new Date()).diff(Utils.toDate(fechaCaducidad), 'months');
        const diffCaducidadDays = moment(new Date()).diff(Utils.toDate(fechaCaducidad), 'days');

        if (fechaCaducidad === '01/01/6000') {
            return {
                rowStyle: {
                    color: 'black',
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid #dddddd'
                },
                textAlign: 'center'
            };
        }

        if (diffCaducidad >= 12) {
            return {
                rowStyle: {
                    color: 'white',
                    backgroundColor: '#222222',
                    borderBottom: '1px solid #dddddd'
                },
                textAlign: 'center'
            };
        }

        if (diffCaducidad >= 6) {
            return {
                rowStyle: {
                    color: 'black',
                    backgroundColor: '#ff7f7f',
                    borderBottom: '1px solid #dddddd'
                },
                textAlign: 'center'
            };
        }

        if (diffCaducidad >= 0) {
            if (diffCaducidad === 0 && diffCaducidadDays < 0) {
                return {
                    rowStyle: {
                        color: 'black',
                        backgroundColor: '#ff9a02',
                        borderBottom: '1px solid #dddddd'
                    },
                    textAlign: 'center'
                };
            }

            return {
                rowStyle: {
                    color: 'black',
                    backgroundColor: '#ffcf78',
                    borderBottom: '1px solid #dddddd'
                },
                textAlign: 'center'
            };
        }

        return cellStyle;
    }

    public globalVisualTransform(value: string, col: ColInterface): string {
        if (col.field === 'activo') {
            return [1, '1', true, 'true', 'Sí'].includes(value) ? 'Sí' : 'No';
        }

        if (Utils.isNumber(value)) {
            return Utils.decimalFormat(value, 2, '.', ',', 3);
        }

        if (col.field === 'fecha_caducidad' && value === '01/01/6000') {
            return '';
        }

        return value;
    }

    public createNavigation(route: string): void {
        this.router.navigate(['dashboard', route]);
    }

    private initFilter(): void {
        this.filter.addFilter((data: TreeNode<TratamientosModel>[] | void) => {
            return (data || [])
                .filter((item) => item)
                .filter((item: TreeNode<TratamientosModel>) => {
                    if ((item as unknown as TratamientosModel).fecha_caducidad === '//') {
                        (item as unknown as TratamientosModel).fecha_caducidad = '-';
                    }

                    return item;
                });
        });
    }

    private buildModalModel(source: TratamientosModel): TratamientosModel {
        const lines = this.decorateLineCaducidad(source.linea ?? []);

        return {
            ...source,
            activo: [1, '1', true, 'true', 'Sí'].includes(source.activo ?? false),
            linea: lines,
            id: String(source.id ?? ''),
            id_cliente: String(source.id_cliente ?? ''),
            id_usuario: String(source.id_usuario ?? ''),
            nombre: String(source.nombre ?? ''),
            ids_productos: lines.map((line) => String(line.producto?.nombre ?? '')).join(';'),
            ids_usos: lines.map((line) => String(line.uso_agente?.uso ?? '')).join(';'),
            nombres_usos: lines.map((line) => String(line.uso_agente?.uso ?? '')).join(';'),
            ids_agentes: lines.map((line) => String(line.uso_agente?.agente ?? '')).join(';'),
            nombres_agentes: lines.map((line) => String(line.uso_agente?.agente ?? '')).join(';'),
            cantidades_cuaderno: lines.map((line) => String(line.cantidad_cuaderno ?? '')).join(';'),
            cantidades_real: lines.map((line) => String(line.cantidad_real ?? '')).join(';'),
            uds_medida: this.getSerializedUnits(source, lines, false),
            uds_medida_cuaderno: this.getSerializedUnits(source, lines, true),
            ocultar_en_receta: lines
                .map((line) => (line.ocultar_en_receta ? 'true' : 'false'))
                .join(';')
        };
    }

    private buildDetailTable(model: TratamientosModel): FormTable {
        const hideDosisCuaderno = StorageManager.checkHiddenDosisCuaderno();

        const header: string[] = [
            'Producto',
            'Uso',
            'Agente'
        ];

        if (!hideDosisCuaderno) {
            header.push(this.env.enableLegalMode ? 'Dosis' : 'C. Cuaderno');
        }

        header.push('F. Caducidad');

        const rows: string[][] = (model.linea ?? []).map((line, index) => {
            const row: string[] = [
                String(line.producto?.nombre ?? ''),
                String(line.uso_agente?.uso ?? ''),
                String(line.uso_agente?.agente ?? '')
            ];

            if (!hideDosisCuaderno) {
                row.push(this.formatCantidadCuaderno(line, model, index));
            }

            row.push(String(line.producto?.vademecum?.fecha_caducidad ?? 'No figura'));

            return row;
        });

        const styles: string[] = [
            '',
            ...(model.linea ?? []).map((line) => String(line.producto?.caducado ?? '#f8f9fa'))
        ];

        return {
            title: '',
            showIndex: false,
            data: [header, ...rows],
            style: styles
        };
    }

    private decorateLineCaducidad(lines: TratamientoLineaModel[]): TratamientoLineaModel[] {
        return lines.map((line) => {
            const decoratedLine: TratamientoLineaModel = {
                ...line,
                producto: {
                    ...(line.producto ?? { nombre: '', materia_activa: '' })
                },
                uso_agente: {
                    ...(line.uso_agente ?? { uso: '', agente: '' })
                }
            };

            const fechaCaducidad = String(decoratedLine.producto?.fecha_caducidad ?? '');
            const diffMonths = moment(new Date()).diff(Utils.toDate(fechaCaducidad), 'months');
            const diffDays = moment(new Date()).diff(Utils.toDate(fechaCaducidad), 'days');

            if (diffMonths >= 12) {
                decoratedLine.producto.caducado = '#222222';
            } else if (diffMonths >= 6) {
                decoratedLine.producto.caducado = '#ff7f7f';
            } else if (diffMonths >= 0) {
                decoratedLine.producto.caducado = diffMonths === 0 && diffDays < 0
                    ? '#ff9a02'
                    : '#ffcf78';
            } else {
                decoratedLine.producto.caducado = '#f8f9fa';
            }

            return decoratedLine;
        });
    }

    private getSerializedUnits(
        model: TratamientosModel,
        lines: TratamientoLineaModel[],
        useCuadernoUnits: boolean
    ): string {
        const source = String(
            useCuadernoUnits
                ? model.uds_medida_cuaderno ?? ''
                : model.uds_medida ?? ''
        );

        const tokens = source.split(';');

        if (tokens.length >= lines.length && source.trim() !== '') {
            return source;
        }

        return lines.map((_line, index) => {
            return String(tokens[index] ?? tokens[0] ?? '');
        }).join(';');
    }

    private formatCantidadCuaderno(
        line: TratamientoLineaModel,
        model: TratamientosModel,
        index: number
    ): string {
        const cantidad = line.cantidad_cuaderno;

        if (cantidad == null || cantidad === 0) {
            return '';
        }

        const cantidadFormatted = Utils.decimalFormat(cantidad, 2, '.', ',', 2);
        const unidadesSource = this.env.showUdsCuaderno
            ? String(model.uds_medida_cuaderno ?? '')
            : String(model.uds_medida ?? '');

        const unidades = unidadesSource.split(';');
        const unidad = String(unidades[index] ?? '').trim();

        return unidad !== ''
            ? `${cantidadFormatted} ${unidad}`
            : cantidadFormatted;
    }
}